<script setup lang="ts">
import { VAlert, VButton, VDropdownMultiple, VIcon, VTextArea, VTextField } from '@/modules/shared/components'
import VSelect from '../../../components/v-select.vue'
import DropZone from '@/modules/filing/components/DropZone.vue'
import { useFileDrop } from '@/modules/filing/composables/use-file-drop'

const props = defineProps<{
  form: any
  v$: any
  raisingStatus: string
  visibilityOptions: { label: string; value: string }[]
  investorOptions: { label: string; value: string }[]
  files: File[]
  existingFiles?: string[]
}>()

const emit = defineEmits<{
  (e: 'update:raisingStatus', value: string): void
  (e: 'update:files', files: File[]): void
}>()

const { fileInput, removeFile, onDrop, onFileSelect, formatSize } = useFileDrop(
  () => props.files,
  (files) => emit('update:files', files),
)

const fileExtension = (name: string): string => name.split('.').pop()?.toUpperCase() ?? 'FILE'

const fundTypeOptions = [
  'Venture Capital',
  'Private Equity',
  'Real Estate',
  'Hedge Fund',
  'Fund of Funds',
  'SPV Fund',
  'Rolling Fund',
  'Other',
].map((s) => ({ label: s, value: s }))

const sectorOptions = [
  'Artificial Intelligence',
  'Biotech / Life Sciences',
  'Climate / Clean Energy',
  'Consumer Products',
  'Cybersecurity',
  'Defense / Aerospace',
  'E-Commerce',
  'EdTech',
  'FinTech',
  'FoodTech',
  'Gaming',
  'Hardware',
  'HealthTech',
  'Infrastructure',
  'InsurTech',
  'Logistics / Supply Chain',
  'Manufacturing',
  'MarketPlace',
  'Media / Entertainment',
  'PropTech / Real Estate',
  'Robotics',
  'SaaS',
  'SpaceTech',
  'Telecom',
  'Transportation',
  'Web3 / Blockchain',
  'Other',
].map((s) => ({ label: s, value: s }))

const stageOptions = [
  'Pre-Seed',
  'Seed',
  'Series A',
  'Series B',
  'Series C+',
  'Growth',
  'Late Stage',
  'Multi-Stage',
  'Agnostic',
].map((s) => ({ label: s, value: s }))

const currencyOptions = [
  { label: 'USD ($)', value: 'USD' },
  { label: 'EUR (€)', value: 'EUR' },
  { label: 'GBP (£)', value: 'GBP' },
]

const raisingStatusOptions = [
  {
    value: 'currently_raising',
    label: 'Currently raising',
    icon: 'TrendingUp',
    description: 'Promote your fund to investors, take commitments and track closing progress.',
  },
  {
    value: 'closed',
    label: 'Closed / Not raising',
    icon: 'CheckCircle',
    description: 'Manage distributions, reporting, and ongoing administration from your dashboard.',
  },
]

defineExpose({
  isValid: async () => {
    return !!props.raisingStatus
  },
})
</script>

<template>
  <div class="space-y-6">
    <div>
      <label class="mb-2 block text-sm font-medium text-gray-700">Is this fund currently raising capital?</label>
      <div class="grid grid-cols-2 gap-3">
        <button
          v-for="option in raisingStatusOptions"
          :key="option.value"
          type="button"
          class="rounded-lg border-none px-5 py-4 text-left shadow-sm ring-1 transition-shadow active:translate-y-px active:shadow-sm"
          :class="
            raisingStatus === option.value
              ? 'bg-[#009192]/[0.08] text-gray-900 ring-[#009192] hover:shadow-md'
              : 'bg-white text-gray-700 ring-gray-700/20 hover:shadow-md hover:ring-gray-700/30'
          "
          @click="$emit('update:raisingStatus', option.value)"
        >
          <div class="mb-1.5 flex items-center gap-2">
            <VIcon :name="option.icon" class="h-5 w-5 text-[#009192]" />
            <span class="text-sm font-semibold">{{ option.label }}</span>
          </div>
          <div class="text-[12px] leading-relaxed text-gray-400">{{ option.description }}</div>
        </button>
      </div>
    </div>

    <template v-if="raisingStatus === 'closed'">
      <div class="my-8 h-px bg-gray-100"></div>
      <VAlert type="info">
        Your fund will be set up for ongoing administration only. You can enable raising at any time from your fund
        dashboard.
      </VAlert>
    </template>

    <div class="my-8 h-px bg-gray-100"></div>

    <div>
      <h3 class="text-sm font-medium text-gray-900">Fund details</h3>
      <p class="mt-1 text-sm text-gray-500">
        Provide details about your fund. This information will be visible to potential investors on your fund profile.
      </p>
    </div>

    <VSelect v-model="form.fund_type" label="Fund type" :options="fundTypeOptions" property="fund_type" :v$="v$" />
    <VTextArea
      v-model="form.company_description"
      label="Fund thesis"
      placeholder="Describe your fund's investment thesis, strategy, and what differentiates your approach..."
      property="company_description"
      :v$="v$"
    />
    <VTextField
      v-model="form.company_website"
      label="Fund website"
      placeholder="https://"
      property="company_website"
      :v$="v$"
    />

    <div class="my-8 h-px bg-gray-100"></div>

    <h3 class="text-sm font-medium text-gray-900">Investment focus</h3>
    <fieldset class="space-y-4 rounded-lg border border-gray-300/50 bg-white p-4">
      <label class="block text-sm font-medium text-gray-800">Target sectors</label>
      <VDropdownMultiple
        aligned="left"
        class="z-auto"
        :options="sectorOptions"
        property="sectors"
        v-model="form.sectors"
        :show-selected-list="true"
      >
        <VButton size="xl" class="w-full">
          <div class="flex items-center justify-between">
            <div>Select one or more sectors...</div>
            <VIcon name="ChevronSelectorVertical" />
          </div>
        </VButton>
      </VDropdownMultiple>
    </fieldset>
    <VSelect
      v-model="form.investment_stage"
      label="Target stage"
      :options="stageOptions"
      property="investment_stage"
      :v$="v$"
    />

    <div class="my-8 h-px bg-gray-100"></div>

    <h3 class="text-sm font-medium text-gray-900">Raise details</h3>
    <div class="grid gap-4 md:grid-cols-2">
      <VTextField v-model="form.raised_amount" label="Target raise" type="currency" property="raised_amount" :v$="v$" />
      <VTextField
        v-model="form.pre_money_valuation_amount"
        label="Minimum to Close"
        type="currency"
        property="pre_money_valuation_amount"
        :v$="v$"
      />
    </div>
    <div class="grid gap-4 md:grid-cols-2">
      <VTextField
        v-model="form.minimum_investment_amount"
        label="Minimum LP commitment"
        type="currency"
        property="minimum_investment_amount"
        :v$="v$"
      />
      <VSelect v-model="form.currency" label="Currency" :options="currencyOptions" />
    </div>
    <VTextField
      v-model="form.expected_close_date"
      label="Target first close date"
      type="date"
      property="expected_close_date"
      :v$="v$"
    />

    <div class="my-8 h-px bg-gray-100"></div>

    <p class="text-sm font-medium text-gray-700">Fund Visibility</p>
    <VSelect
      v-model="form.visibility"
      label="Who should see this fund?"
      :options="visibilityOptions"
      property="visibility"
      :v$="v$"
    />
    <fieldset v-if="form.visibility === 'selected'" class="space-y-2">
      <label class="mb-1 block text-sm font-medium text-gray-700">Select investors</label>
      <VDropdownMultiple
        aligned="left"
        class="z-auto"
        :options="investorOptions"
        property="invited_investor_ids"
        v-model="form.invited_investor_ids"
        :show-selected-list="true"
      >
        <VButton size="xl" class="w-full" type="button">
          <div class="flex items-center justify-between">
            <div>Select investors...</div>
            <VIcon name="ChevronSelectorVertical" />
          </div>
        </VButton>
      </VDropdownMultiple>
    </fieldset>

    <div class="my-8 h-px bg-gray-100"></div>

    <div>
      <h3 class="mb-1 text-sm font-medium text-gray-700">Supporting documents</h3>
      <p class="mb-3 text-sm text-gray-500">
        Upload pitch decks, PPMs, term sheets, or other relevant documents for potential investors.
      </p>

      <div v-if="existingFiles?.length || files.length" class="mb-3 space-y-0">
        <div
          v-for="(name, i) in existingFiles"
          :key="'existing-' + i"
          class="flex items-center gap-2.5 border-b border-gray-50 py-2 last:border-0"
        >
          <div
            class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded bg-red-50 text-[7px] font-extrabold text-red-600"
          >
            {{ fileExtension(name) }}
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold text-gray-800">{{ name }}</div>
          </div>
        </div>
        <div
          v-for="(file, i) in files"
          :key="'new-' + i"
          class="flex items-center gap-2.5 border-b border-gray-50 py-2 last:border-0"
        >
          <div
            class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded bg-red-50 text-[7px] font-extrabold text-red-600"
          >
            {{ fileExtension(file.name) }}
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold text-gray-800">{{ file.name }}</div>
            <div class="text-xs text-gray-400">{{ formatSize(file.size) }}</div>
          </div>
          <button
            class="flex-shrink-0 rounded px-1.5 py-0.5 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            @click="removeFile(i)"
          >
            &times;
          </button>
        </div>
      </div>

      <DropZone state="default" @click="fileInput?.click()" @drop.prevent="onDrop" @dragover.prevent />
      <input ref="fileInput" type="file" multiple class="hidden" @change="onFileSelect" />
    </div>
  </div>
</template>
